import { useNavigate } from 'react-router-dom';

import { Badge, Dropdown } from '../common';
import { useModel } from '../../context/ModelContext';
import { formatMetric } from '../../utils/format';
import './layout.css';

/**
 * The always-visible answer to "which model produced this?".
 *
 * Selecting a version here loads it on the GPU, so the dropdown shows why a
 * version cannot be selected rather than hiding it — a checkpoint with no
 * attribute set is the commonest state right after an import, and the fix is one
 * click away in Settings.
 *
 * With vLLM serving, selecting is per person and free when the version's base
 * model is already loaded; the badges say which ones are.
 */
const SERVING_BADGES = {
  ready: { tone: 'ok', label: 'serving' },
  starting: { tone: 'warn', label: 'loading…' },
  draining: { tone: 'warn', label: 'unloading…' },
  failed: { tone: 'danger', label: 'failed' },
};

export function VersionPill() {
  const {
    versions,
    activeVersion,
    worker,
    switching,
    selectVersion,
    loading,
    isVllm,
    inferenceAlive,
    inferenceProblem,
  } = useModel();
  const navigate = useNavigate();

  const servingState = activeVersion?.serving?.state;
  const busy = isVllm ? servingState === 'starting' : Boolean(worker?.busy);
  const alive = isVllm ? inferenceAlive : Boolean(worker) && !worker.error;

  const dotClass = switching || busy ? 'vpill__dot--busy' : alive ? 'vpill__dot--live' : '';
  const attributeCount = activeVersion?.attribute_set_detail?.attribute_count;
  const metric = activeVersion?.training?.metric_value;

  const meta = [
    attributeCount ? `${attributeCount} attrs` : activeVersion?.attribute_set,
    metric !== null && metric !== undefined
      ? `${activeVersion.training.metric_name || 'metric'} ${formatMetric(metric)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Dropdown
      align="left"
      trigger={({ toggle }) => (
        <button
          type="button"
          className={`vpill ${activeVersion ? '' : 'vpill--empty'}`}
          onClick={toggle}
          title={
            isVllm
              ? alive
                ? `vLLM · ${activeVersion?.serving?.weights_label || ''}`
                : inferenceProblem || 'Select a version'
              : alive
                ? `Worker: ${worker.engine} on ${worker.gpu_name || worker.device}`
                : 'Inference worker is not reachable'
          }
        >
          <span className={`vpill__dot ${dotClass}`} aria-hidden="true" />
          <span className="vpill__label">
            {loading
              ? 'Loading versions…'
              : activeVersion
                ? activeVersion.id
                : 'Select a model version'}
          </span>
          {meta && <span className="vpill__meta">· {meta}</span>}
          <span className="vpill__chev" aria-hidden="true">
            ▾
          </span>
        </button>
      )}
      render={({ close }) => (
        <div className="vpicker">
          <div className="section-label" style={{ padding: '6px 10px 8px' }}>
            Model versions
          </div>

          {versions.length === 0 && (
            <div className="dropdown__item" style={{ color: 'var(--fg-muted)' }}>
              No checkpoints found. Import one in Settings → Model versions.
            </div>
          )}

          {versions.map((version) => {
            const blocked = !version.is_ready;
            const servingBadge = isVllm ? SERVING_BADGES[version.serving?.state] : null;
            return (
              <button
                key={version.id}
                type="button"
                className={[
                  'vpicker__item',
                  version.id === activeVersion?.id && 'vpicker__item--active',
                  blocked && 'vpicker__item--blocked',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={async () => {
                  if (blocked) {
                    navigate('/settings/versions');
                    close();
                    return;
                  }
                  close();
                  await selectVersion(version.id);
                }}
              >
                <div className="vpicker__row">
                  <span className="vpicker__name">{version.label || version.id}</span>
                  {!isVllm && version.is_active && <Badge tone="ok">loaded</Badge>}
                  {servingBadge && <Badge tone={servingBadge.tone}>{servingBadge.label}</Badge>}
                  {isVllm && !servingBadge && !blocked && (
                    <Badge title="Its base model is not on the GPU">not loaded</Badge>
                  )}
                  {blocked && <Badge tone="warn">needs metadata</Badge>}
                </div>
                <div className="vpicker__sub">
                  {version.id}
                  {version.attribute_set ? ` · ${version.attribute_set}` : ''}
                  {version.training?.epoch !== null && version.training?.epoch !== undefined
                    ? ` · epoch ${version.training.epoch}`
                    : ''}
                  {version.training?.metric_value !== null &&
                  version.training?.metric_value !== undefined
                    ? ` · ${version.training.metric_name} ${formatMetric(version.training.metric_value)}`
                    : ''}
                </div>
                {blocked && version.manifest_errors?.length > 0 && (
                  <div className="vpicker__sub" style={{ color: 'var(--state-warn)' }}>
                    {version.manifest_errors[0]}
                  </div>
                )}
              </button>
            );
          })}

          <div className="dropdown__divider" />
          <button
            type="button"
            className="dropdown__item"
            onClick={() => {
              navigate('/settings/versions');
              close();
            }}
          >
            Manage versions →
          </button>
        </div>
      )}
    />
  );
}
